import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Lightbulb, X, Heart, Send, Check } from 'lucide-react';
import { liteApi } from '@/utils/lite/liteApi';

/**
 * Lite "Wishlist" widget — anonymous "what would help" notes from users.
 *
 * Designed to set clear expectations:
 *  - Header: "What would make PlayFirst better for you?"
 *  - Sub: "Read by Michaela every Friday. Not real-time."
 *  - Optional email field: "Want a reply? (optional)"
 *  - Confirmation: "Thanks — Michaela reads these on Fridays."
 *  - Urgent prompt: "For urgent help, email michaela@playfirstapp.com."
 *
 * Two layouts:
 *  - Inline (default): a small banner that expands when clicked
 *  - Card: a full-width card variant for the Lite hub
 *
 * Privacy: message + optional email go to /api/lite/wishlist (90-day TTL).
 * Email is hashed; plaintext only kept if user opts in for a reply.
 */
export default function LiteWishlist({ variant = 'inline', sourcePage = '' }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [wantReply, setWantReply] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setError('');
    if (message.trim().length < 4) {
      setError('Add a few more words so I know what to act on.');
      return;
    }
    setSubmitting(true);
    try {
      await liteApi.wishlist({
        message: message.trim(),
        email: wantReply && email.trim() ? email.trim() : undefined,
        page: sourcePage || (typeof window !== 'undefined' ? window.location.pathname : ''),
      });
      setDone(true);
      // Auto-collapse after a moment so it's not in the way
      setTimeout(() => { setOpen(false); }, 4000);
    } catch (e) {
      setError(e.message || 'Could not save your note. Try again?');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setMessage(''); setEmail(''); setWantReply(false); setError(''); setDone(false);
  };

  if (variant === 'card') {
    return (
      <div
        className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-5"
        data-testid="lite-wishlist-card"
      >
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
            <Lightbulb className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-stone-900">
              What would make PlayFirst better for you?
            </h3>
            <p className="text-xs text-stone-600 mt-0.5">
              Michaela reads every reply on Fridays. Not real-time — for urgent help, email{' '}
              <a href="mailto:michaela@playfirstapp.com" className="underline hover:no-underline">
                michaela@playfirstapp.com
              </a>.
            </p>
          </div>
        </div>
        {done ? (
          <DoneBlock onAnother={reset} />
        ) : (
          <WishlistForm
            message={message} setMessage={setMessage}
            email={email} setEmail={setEmail}
            wantReply={wantReply} setWantReply={setWantReply}
            submit={submit} submitting={submitting} error={error}
          />
        )}
      </div>
    );
  }

  // Inline (default): small expandable banner
  return (
    <div className="rounded-xl border border-stone-200 bg-white" data-testid="lite-wishlist-inline">
      {!open && !done && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-stone-50 transition rounded-xl"
          data-testid="lite-wishlist-open-btn"
        >
          <Lightbulb className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-stone-900">
              What would make PlayFirst better?
            </div>
            <div className="text-xs text-stone-500">
              Read by Michaela every Friday — not real-time.
            </div>
          </div>
          <span className="text-xs text-amber-700 font-medium flex-shrink-0">Tell us →</span>
        </button>
      )}

      {open && !done && (
        <div className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h4 className="text-sm font-semibold text-stone-900">
                What would help?
              </h4>
              <p className="text-xs text-stone-500 mt-0.5">
                Michaela reads on Fridays. For urgent help:{' '}
                <a href="mailto:michaela@playfirstapp.com" className="underline">
                  michaela@playfirstapp.com
                </a>
              </p>
            </div>
            <button
              type="button"
              onClick={() => { reset(); setOpen(false); }}
              className="text-stone-400 hover:text-stone-600"
              data-testid="lite-wishlist-close-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <WishlistForm
            message={message} setMessage={setMessage}
            email={email} setEmail={setEmail}
            wantReply={wantReply} setWantReply={setWantReply}
            submit={submit} submitting={submitting} error={error}
          />
        </div>
      )}

      {done && (
        <div className="p-4">
          <DoneBlock onAnother={reset} />
        </div>
      )}
    </div>
  );
}

function WishlistForm({
  message, setMessage, email, setEmail, wantReply, setWantReply,
  submit, submitting, error,
}) {
  return (
    <>
      <Textarea
        rows={3}
        placeholder="What's the toughest paperwork moment in your week? What's missing from PlayFirst? Anything…"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        className="resize-none mb-2 text-sm"
        data-testid="lite-wishlist-message"
        maxLength={2000}
      />
      <div className="flex items-center gap-2 mb-2">
        <input
          type="checkbox"
          id="wishlist-want-reply"
          checked={wantReply}
          onChange={(e) => setWantReply(e.target.checked)}
          className="rounded border-stone-300"
          data-testid="lite-wishlist-want-reply"
        />
        <label htmlFor="wishlist-want-reply" className="text-xs text-stone-600 cursor-pointer">
          Want a reply? (optional — adds your email)
        </label>
      </div>
      {wantReply && (
        <Input
          type="email"
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mb-2 text-sm"
          data-testid="lite-wishlist-email"
        />
      )}
      {error && (
        <div className="text-xs text-red-600 mb-2">{error}</div>
      )}
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-stone-400">
          90-day retention · {wantReply ? 'Email kept for reply' : 'Anonymous'}
        </p>
        <Button
          size="sm"
          onClick={submit}
          disabled={submitting || message.trim().length < 4}
          className="rounded-full"
          data-testid="lite-wishlist-submit"
        >
          <Send className="w-3.5 h-3.5 mr-1.5" />
          {submitting ? 'Sending…' : 'Send'}
        </Button>
      </div>
    </>
  );
}

function DoneBlock({ onAnother }) {
  return (
    <div className="text-center py-3" data-testid="lite-wishlist-done">
      <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 mx-auto flex items-center justify-center mb-2">
        <Check className="w-5 h-5" />
      </div>
      <div className="text-sm font-medium text-stone-900 flex items-center justify-center gap-1">
        Thanks <Heart className="w-3.5 h-3.5 text-pink-500" />
      </div>
      <p className="text-xs text-stone-600 mt-1">
        Michaela reads these on Fridays.
      </p>
      <button
        type="button"
        onClick={onAnother}
        className="text-xs text-amber-700 hover:underline mt-2"
        data-testid="lite-wishlist-another-btn"
      >
        Add another note
      </button>
    </div>
  );
}
